import React, { useState } from 'react';
import { FirebaseService } from '../../services/FirebaseService';
import { getAuthErrorMessage } from '../../utils/authErrors';
import './ForgotPassword.css';

interface ForgotPasswordProps {
  onBack: () => void;
  onSuccess: () => void;
}

export const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onBack, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Basic validation
    if (!email.trim()) {
      setError('Please enter your email address');
      setIsLoading(false);
      return;
    }

    try {
      await FirebaseService.resetPassword(email.trim());
      setIsSuccess(true);
    } catch (error: any) {
      setError(getAuthErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="forgot-password-container">
        <div className="forgot-password-card">
          <div className="forgot-password-header">
            <h1 className="forgot-password-title">📧 Check Your Email</h1>
            <p className="forgot-password-subtitle">
              Password reset instructions sent!
            </p>
          </div>

          <div className="success-content">
            <div className="success-message">
              <p>
                We've sent password reset instructions to <strong>{email}</strong>
              </p>
              <p>
                Please check your email and follow the link to reset your password.
              </p>
            </div>

            <div className="forgot-password-actions">
              <button
                type="button"
                onClick={onBack}
                className="btn btn-secondary back-button"
              >
                Back to Sign In
              </button>
              
              <button
                type="button"
                onClick={() => {
                  setIsSuccess(false);
                  setEmail('');
                }}
                className="link-button"
              >
                Resend Email
              </button>
            </div>
          </div>

          <div className="forgot-password-info">
            <p className="info-text">
              Didn't receive an email? Check your spam folder or try again with a different email address.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="forgot-password-container">
      <div className="forgot-password-card">
        <div className="forgot-password-header">
          <h1 className="forgot-password-title">🔐 Reset Password</h1>
          <p className="forgot-password-subtitle">
            Enter your email to receive reset instructions
          </p>
        </div>

        <form onSubmit={handleSubmit} className="forgot-password-form">
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="input"
              placeholder="Enter your email address"
              autoComplete="email"
            />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="forgot-password-actions">
            <button
              type="submit"
              disabled={isLoading || !email.trim()}
              className="btn btn-primary reset-button"
            >
              {isLoading ? 'Sending...' : 'Send Reset Email'}
            </button>

            <button
              type="button"
              onClick={onBack}
              className="btn btn-secondary back-button"
            >
              Back to Sign In
            </button>
          </div>
        </form>

        <div className="forgot-password-info">
          <p className="info-text">
            Remember your password? 
            <button onClick={onBack} className="link-button">
              Sign In
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};
